import React from "react"
import { debounce } from "lodash-es"
import Sign from "@/signomatic/app/sign"
import type { State } from "@/signomatic/store/reducers"
import { connect } from "react-redux"
import { withTranslation, type WithTranslation } from "react-i18next"
import { isTablet } from "@/common/util"
import Checkbox from "./checkbox"
import type { EditingState } from "@/common/client_data"
import ClientData from "@/common/client_data"
import env from "@/common/env"
import { graphql } from "@/api"
import { Query } from "@apollo/client/react/components"
import type { PriceQuery, PriceQueryVariables } from "@/api/graphql"

const priceDoc = graphql(`
  query Price($signcode: String!, $numberOfSigns: String, $currentCount: Int, $editingId: Int) {
    price(signcode: $signcode, numberOfSigns: $numberOfSigns, currentCount: $currentCount, editingId: $editingId) {
      regularPrice
      discountedPrice
      discountPercent
    }
  }
`)

interface PriceOwnProps {
  sign: Sign
  numberOfSigns?: number
  currentCount?: number
  editingId?: number
}

interface PriceStateProps {
  editing: EditingState | null
  signState: SignState | null
}

type PriceProps = PriceStateProps & PriceOwnProps & WithTranslation

interface PriceState {
  signState: SignState | null
  signcode: string | null
  signType: string | null
}

class Price extends React.PureComponent<PriceProps, PriceState> {
  readonly state: PriceState = { signState: null, signcode: null, signType: null }

  private debouncedUpdate = debounce(
    async (state: SignState) => {
      let signcode = this.state.signcode

      state.width = state.forcedWidth || state.width
      state.height = state.forcedHeight || state.height

      if (this.statePricedChanged(state)) {
        const code = await this.props.sign.signcode(true)
        if (code) signcode = JSON.stringify(code)
      }

      this.setState({ signState: state, signType: this.props.sign.signType, signcode })
    },
    300,
    { leading: false }
  )

  async componentDidMount() {
    const code = await this.props.sign.signcode(true)
    const signcode = code ? JSON.stringify(code) : null
    this.setState({ signcode })

    this.props.sign.onSignChanged.subscribe(this.debouncedUpdate)
  }

  componentWillUnmount() {
    this.props.sign.onSignChanged.unsubscribe(this.debouncedUpdate)
  }

  statePricedChanged(newState: SignState) {
    const signType = this.props.sign.signType
    const state = this.state.signState

    if (!state || !signType) {
      return true
    }

    if (signType !== this.state.signType) {
      return true
    }

    // size
    if (!["color_badge", "brass_sign"].includes(signType)) {
      if (newState.width !== state.width || newState.height !== state.height) {
        return true
      }
    }

    // images
    if (!["color_badge", "brass_sign", "rollup"].includes(signType)) {
      const oldCount = state.objects.filter(obj => Sign.isImage(obj)).length
      const newCount = newState.objects.filter(obj => Sign.isImage(obj)).length

      if (oldCount !== newCount) {
        return true
      }
    }

    // fixmethod
    if (!["banner", "vinyl_text", "magnetic_sign"].includes(signType)) {
      if (state.fixmethod !== newState.fixmethod) {
        return true
      }
    }

    // border
    if (!["color_badge", "brass_sign", "rollup"].includes(signType)) {
      if (state.border !== newState.border) {
        return true
      }
    }

    // background
    if (["plastic_sign", "name_sign", "aluminium_sign", "double_sided_sign", "wood_sign"].includes(signType)) {
      if (state.color !== newState.color) {
        return true
      }
    }

    // material
    if (
      ["rollup", "color_badge", "brass_sign", "aluminium_sign", "double_sided_sign", "label_plates"].includes(signType)
    ) {
      if (state.material !== newState.material) {
        return true
      }
    }

    // thickness
    if (["plastic_sign"].includes(signType)) {
      if (state.thickness !== newState.thickness) {
        return true
      }
    }

    if (state.badgeName !== newState.badgeName) {
      return true
    }

    if (state.doubleSided !== newState.doubleSided) {
      return true
    }

    return false
  }

  renderPriceCheckbox() {
    const { sign, editing, signState } = this.props

    const disabled = (editing && editing.fixedPrice) || false

    if (!editing || !editing.admin || !signState) {
      return null
    }

    return (
      <div className="option">
        <Checkbox
          id="sign_price_fixed"
          checked={signState.fixedPrice}
          onChange={() => sign.updateState({ fixedPrice: !signState.fixedPrice })}
          disabled={disabled}
        />
        <label htmlFor="sign_price_fixed">Pris låst</label>
      </div>
    )
  }

  render() {
    const { signcode } = this.state
    const { editing, signState, t } = this.props
    const vatText = ClientData.businessRole ? t("order.exc_vat") : t("order.inc_vat")

    if (editing && (editing.fixedPrice || (signState && signState.fixedPrice))) {
      return <>{this.renderPriceCheckbox()}</>
    }

    if (!signcode) {
      return null
    }

    return (
      <Query<PriceQuery, PriceQueryVariables>
        query={priceDoc}
        variables={{
          signcode,
          numberOfSigns: this.props.numberOfSigns?.toString(),
          currentCount: this.props.currentCount,
          editingId: editing?.id,
        }}
      >
        {({ loading, error, data, previousData }) => {
          const currentData = data || previousData
          if (error || !currentData || !currentData.price) {
            return null
          }

          const regularPrice = env.TEST && loading ? "--" : currentData.price.regularPrice

          const showDiscount = currentData.price.discountedPrice && !editing

          let mainPrice = regularPrice
          if (currentData.price.discountedPrice && !editing) {
            mainPrice = currentData.price.discountedPrice
          }

          return (
            <>
              {isTablet() ? (
                <div id="sign_price_field" className="flex flex-col">
                  {this.props.sign.signType !== "" && (
                    <h1 className="sign-type paragraph-2xs bold right mb-0 self-end">
                      {t(
                        `sign_type.${
                          this.props.signState?.metaProduct
                            ? this.props.signState.metaProduct
                            : this.props.sign.signType
                        }`
                      )}
                    </h1>
                  )}
                  <div className="flex items-baseline self-end">
                    <span className="paragraph-sm bold mb-0 mr-1 whitespace-nowrap">{mainPrice}</span>
                    <span className="paragraph-2xs mb-0 whitespace-nowrap">{vatText}</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="flex flex-row items-center">
                    <div id="sign_price_field" className="mb-0 mr-2 text-base font-semibold xl:text-xl">
                      {mainPrice}
                    </div>

                    {showDiscount && (
                      <>
                        <div
                          id="regular_price_field"
                          className="mb-0 mr-2 whitespace-nowrap text-base leading-6 text-ink-50 line-through xl:text-xl"
                        >
                          {regularPrice}
                        </div>

                        <div
                          id="discount_percent_field"
                          className="blue-badge group flex h-6 min-w-fit items-center whitespace-nowrap rounded bg-blue-200"
                        >
                          <div className="px-2">
                            <div className="text-xs font-semibold text-blue-600">
                              {t("order.discount_badge", { discount: currentData.price.discountPercent })}%
                            </div>
                          </div>
                          <div className="discount-tooltip height-[55px] absolute bottom-[66px] hidden w-[310px] rounded-lg bg-ink-85 px-3 py-2 group-hover:block">
                            <div className="absolute -bottom-[14%] left-[17px] origin-center rotate-45 border-x-[8px] border-t-[16px] border-solid border-transparent bg-ink-85"></div>
                            <span className="tooltip-text paragraph-2xs light mb-0 block whitespace-normal decoration-white">
                              {t("order.discount_tooltip")}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="my-[1px] flex items-center">
                    <span className="whitespace-nowrap text-xs">{t("order.price_per")}</span>
                    <span className="mx-1 font-['Lato'] text-xs">&#183;</span>
                    <span className="whitespace-nowrap text-xs">{vatText}</span>
                  </div>
                </div>
              )}
              {this.renderPriceCheckbox()}
            </>
          )
        }}
      </Query>
    )
  }
}

export default connect(
  ({ editing, signState }: State): PriceStateProps => ({
    editing,
    signState,
  }),
  {}
)(withTranslation()(Price))
